import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MuiAutocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { Controller } from "react-hook-form";

export default function SearchableAutoComplete({
  control,
  data,
  payload,
  setPayload,
  label,
  loading,
  name,
  multiple,
  defaultValue,
  hasNextPage,
  fields,
  errors,
  onChangeEvent,
  searchInputChange,
  searchLength = 3,
  disabled = false,
}) {
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState("");
  const [position, setPosition] = useState(0);
  const [listboxNode, setListboxNode] = useState(null);
  const [dValue, setDValue] = useState(multiple ? [] : "");

  const required = fields?.[name] !== undefined;

  useEffect(() => {
    setDValue(defaultValue ?? (multiple ? [] : ""));
  }, [defaultValue, multiple]);

  useEffect(() => {
    if (listboxNode) {
      listboxNode.scrollTop = position;
    }
  }, [position, listboxNode]);

  const loadMoreResults = () => {
    if (hasNextPage) {
      const nextPage = page + 1;
      setPage(nextPage);
      setPayload({
        ...payload,
        pagination: {
          pageNumber: nextPage,
          pageSize: 100,
        },
      });
    }
  };

  const handleScroll = (event) => {
    const node = event.currentTarget;
    setListboxNode(node);

    const x = node.scrollTop + node.clientHeight;
    if (node.scrollHeight - x <= 1) {
      setPosition(x);
      loadMoreResults();
    }
  };

  const handleInputChange = (_, value, reason) => {
    setInputValue(value);

    if (reason === "input" && value.length >= searchLength && searchInputChange) {
      const found = data.some((item) => item.label.toLowerCase().includes(value.toLowerCase()));
      if (!found) {
        setPage(1);
        searchInputChange(value);
      }
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={dValue}
      render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
        <MuiAutocomplete
          {...field}
          multiple={multiple}
          options={data}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={(_, newValue) => {
            if (onChangeEvent) onChangeEvent(name, newValue);
            else onChange(newValue);
            setInputValue(""); // Clear input after selection
          }}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) return options;
            return options.filter((option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase()),
            );
          }} // Disable MUI filtering
          loading={loading}
          autoHighlight
          openOnFocus
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                whiteSpace: "normal", // allow wrapping
                wordBreak: "break-word",
                maxWidth: "100%",
              }}
            >
              {option.label}
            </Box>
          )}
          ListboxProps={{ onScroll: handleScroll }}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              inputRef={ref}
              name={name}
              variant="outlined"
              fullWidth
              InputLabelProps={{ required }}
              error={!!error}
              helperText={error?.message}
              inputProps={{
                ...params.inputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
}
